import {
  classifyPublicationRoute,
  PUBLICATION_ROUTE_CLASSES,
} from './publicationRoutePolicy.js';

export const PUBLIC_TELEMETRY_CONTRACT_VERSION = 'f9.4-v1';
export const PUBLIC_TELEMETRY_SOURCE = 'gameplaysync-web';
export const PUBLIC_TELEMETRY_SERVER_PROPERTIES = Object.freeze([
  'gs_session_id',
  'gs_marketing_consent',
  'gs_environment',
]);

export const PUBLIC_TELEMETRY_EVENTS = Object.freeze({
  SESSION_STARTED: 'telemetry_session_started',
  CONSENT_GRANTED: 'telemetry_consent_granted',
  COOKIE_PREFERENCES_OPENED: 'cookie_preferences_opened',
  COOKIE_PREFERENCES_CLOSED: 'cookie_preferences_closed',
  SURFACE_VIEW: 'public_surface_view',
  ACTION_SELECTED: 'public_action_selected',
});

export const PUBLIC_TELEMETRY_ACTIONS = Object.freeze({
  LANDING_GAMES_SEARCH: 'landing_games_search',
  OPEN_GAMES: 'open_games',
  OPEN_GAME_DETAILS: 'open_game_details',
  OPEN_NEWS_ARCHIVE: 'open_news_archive',
  OPEN_NEWS_ARTICLE: 'open_news_article',
  OPEN_FAQ_CATEGORY: 'open_faq_category',
  OPEN_FAQ_ARTICLE: 'open_faq_article',
  START_ILLEGAL_CONTENT_REPORT: 'start_illegal_content_report',
  NEWSLETTER_SIGNUP_ACCEPTED: 'newsletter_signup_accepted',
});

const ACTIVE = 'active_contract';

const freezeSurface = ({ routeTemplates, ...surface }) => Object.freeze({
  ...surface,
  routeTemplates: Object.freeze(routeTemplates),
});

export const PUBLIC_TELEMETRY_SURFACE_CATALOG = Object.freeze([
  freezeSurface({
    surfaceId: 'landing',
    routeTemplates: ['/'],
    kpiId: 'landing_reach',
    collectionState: ACTIVE,
  }),
  freezeSurface({
    surfaceId: 'games_catalog',
    routeTemplates: ['/games'],
    kpiId: 'games_catalog_reach',
    collectionState: ACTIVE,
  }),
  freezeSurface({
    surfaceId: 'game_details',
    routeTemplates: ['/games/:slug'],
    kpiId: 'game_details_reach',
    collectionState: ACTIVE,
  }),
  freezeSurface({
    surfaceId: 'news_index',
    routeTemplates: ['/news'],
    kpiId: 'news_index_reach',
    collectionState: ACTIVE,
  }),
  freezeSurface({
    surfaceId: 'news_archive',
    routeTemplates: ['/news/archive'],
    kpiId: 'news_archive_reach',
    collectionState: ACTIVE,
  }),
  freezeSurface({
    surfaceId: 'news_article',
    routeTemplates: ['/news/:slug'],
    kpiId: 'news_article_reach',
    collectionState: ACTIVE,
  }),
  freezeSurface({
    surfaceId: 'support_center',
    routeTemplates: ['/support'],
    kpiId: 'support_center_reach',
    collectionState: ACTIVE,
  }),
  freezeSurface({
    surfaceId: 'support_report',
    routeTemplates: ['/support/report-illegal-content'],
    kpiId: 'support_report_reach',
    collectionState: ACTIVE,
  }),
  freezeSurface({
    surfaceId: 'support_faq',
    routeTemplates: [
      '/support/faq',
      '/support/faq/:segment',
      '/support/faq/:category/:slug',
    ],
    kpiId: 'support_faq_reach',
    collectionState: ACTIVE,
  }),
  freezeSurface({
    surfaceId: 'legal',
    routeTemplates: ['/legal', '/legal/:slug'],
    kpiId: 'legal_reach',
    collectionState: ACTIVE,
  }),
  freezeSurface({
    surfaceId: 'deal_finder',
    routeTemplates: ['/deal-finder', '/deal-finder/outbound/:offerId'],
    kpiId: 'deal_finder_reach',
    collectionState: 'parked_f2',
  }),
  freezeSurface({
    surfaceId: 'public_auth',
    routeTemplates: ['/login', '/signup', '/reset-password', '/auth/callback'],
    kpiId: 'public_auth_reach',
    collectionState: 'prepared_gate_required',
  }),
]);

const freezeAction = ({ allowedSurfaceIds, ...action }) => Object.freeze({
  ...action,
  allowedSurfaceIds: Object.freeze(allowedSurfaceIds),
});

export const PUBLIC_TELEMETRY_ACTION_CATALOG = Object.freeze([
  freezeAction({
    actionId: PUBLIC_TELEMETRY_ACTIONS.LANDING_GAMES_SEARCH,
    allowedSurfaceIds: ['landing'],
    operationalQuestion: 'Does the landing search start a Games journey?',
  }),
  freezeAction({
    actionId: PUBLIC_TELEMETRY_ACTIONS.OPEN_GAMES,
    allowedSurfaceIds: ['landing'],
    operationalQuestion: 'Does the landing call to action move visitors into Games?',
  }),
  freezeAction({
    actionId: PUBLIC_TELEMETRY_ACTIONS.OPEN_GAME_DETAILS,
    allowedSurfaceIds: ['landing', 'games_catalog', 'game_details'],
    operationalQuestion: 'Do public game lists and related games open a detail page?',
  }),
  freezeAction({
    actionId: PUBLIC_TELEMETRY_ACTIONS.OPEN_NEWS_ARCHIVE,
    allowedSurfaceIds: ['landing', 'news_index'],
    operationalQuestion: 'Do visitors choose to browse the full News archive?',
  }),
  freezeAction({
    actionId: PUBLIC_TELEMETRY_ACTIONS.OPEN_NEWS_ARTICLE,
    allowedSurfaceIds: ['landing', 'news_index', 'news_archive', 'game_details'],
    operationalQuestion: 'Do public News lists and related content open an article?',
  }),
  freezeAction({
    actionId: PUBLIC_TELEMETRY_ACTIONS.OPEN_FAQ_CATEGORY,
    allowedSurfaceIds: ['support_center', 'support_faq'],
    operationalQuestion: 'Do visitors start a self-service support path?',
  }),
  freezeAction({
    actionId: PUBLIC_TELEMETRY_ACTIONS.OPEN_FAQ_ARTICLE,
    allowedSurfaceIds: ['support_center', 'support_faq'],
    operationalQuestion: 'Do visitors open a self-service support guide?',
  }),
  freezeAction({
    actionId: PUBLIC_TELEMETRY_ACTIONS.START_ILLEGAL_CONTENT_REPORT,
    allowedSurfaceIds: ['support_center'],
    operationalQuestion: 'Is the public illegal-content reporting path started?',
  }),
  freezeAction({
    actionId: PUBLIC_TELEMETRY_ACTIONS.NEWSLETTER_SIGNUP_ACCEPTED,
    allowedSurfaceIds: [
      'landing',
      'games_catalog',
      'game_details',
      'news_index',
      'news_archive',
      'news_article',
      'support_center',
      'support_report',
      'support_faq',
    ],
    operationalQuestion: 'Does the newsletter service accept a public signup request?',
  }),
]);

const NORMALIZED_DURATION_BUCKETS = Object.freeze([
  'under_15m',
  '15m_to_1h',
  '1h_to_4h',
  '4h_to_24h',
  'over_24h',
  'not_available',
]);

const freezePreparedJourney = ({ allowedStatuses, ...journey }) => Object.freeze({
  ...journey,
  allowedProperties: Object.freeze(['status', 'duration_bucket']),
  allowedStatuses: Object.freeze(allowedStatuses),
  allowedDurationBuckets: NORMALIZED_DURATION_BUCKETS,
});

// These names and normalized fields are reserved for the owning cutover gates.
// They are intentionally absent from EVENT_PROPERTY_VALIDATORS, so merely
// importing this catalog can never make them collectable.
export const PUBLIC_TELEMETRY_PREPARED_GATE_CATALOG = Object.freeze([
  freezePreparedJourney({
    journeyId: 'deal_finder',
    eventName: 'deal_finder_outcome',
    collectionState: 'parked_f2',
    requiredOwnerGate: 'f2_deal_finder_public_cutover',
    allowedStatuses: ['loaded', 'empty', 'failed', 'outbound_started'],
  }),
  freezePreparedJourney({
    journeyId: 'public_auth',
    eventName: 'public_auth_outcome',
    collectionState: 'prepared_gate_required',
    requiredOwnerGate: 'f1_public_auth_cutover',
    allowedStatuses: ['started', 'completed', 'failed'],
  }),
  freezePreparedJourney({
    journeyId: 'public_participation',
    eventName: 'public_participation_outcome',
    collectionState: 'prepared_gate_required',
    requiredOwnerGate: 'f1_public_participation_cutover',
    allowedStatuses: ['started', 'completed', 'failed'],
  }),
  freezePreparedJourney({
    journeyId: 'moderation',
    eventName: 'moderation_report_outcome',
    collectionState: 'prepared_gate_required',
    requiredOwnerGate: 'f1_moderation_outcome_cutover',
    allowedStatuses: ['accepted', 'in_review', 'resolved', 'rejected', 'timed_out'],
  }),
]);

const ACTIVE_SURFACE_ROUTE_PAIRS = new Set(
  PUBLIC_TELEMETRY_SURFACE_CATALOG
    .filter((surface) => surface.collectionState === ACTIVE)
    .flatMap((surface) => surface.routeTemplates.map(
      (routeTemplate) => `${surface.surfaceId}\u0000${routeTemplate}`,
    )),
);

const ACTIVE_ACTION_SURFACE_PAIRS = new Set(
  PUBLIC_TELEMETRY_ACTION_CATALOG.flatMap((action) => action.allowedSurfaceIds.map(
    (surfaceId) => `${action.actionId}\u0000${surfaceId}`,
  )),
);

const buildSurface = (surfaceId, routeTemplate) => ({
  surface_id: surfaceId,
  route_template: routeTemplate,
});

export const resolvePublicTelemetrySurface = (value) => {
  const route = classifyPublicationRoute(value);
  if (route.routeClass !== PUBLICATION_ROUTE_CLASSES.PUBLIC) {
    return null;
  }

  const { pathname } = route;
  if (
    pathname === '/deal-finder' ||
    pathname.startsWith('/deal-finder/outbound/')
  ) {
    return null;
  }

  if (pathname === '/') {
    return buildSurface('landing', '/');
  }
  if (pathname === '/games') {
    return buildSurface('games_catalog', '/games');
  }
  if (pathname === '/news') {
    return buildSurface('news_index', '/news');
  }
  if (pathname === '/news/archive') {
    return buildSurface('news_archive', '/news/archive');
  }
  if (pathname === '/support') {
    return buildSurface('support_center', '/support');
  }
  if (pathname === '/support/report-illegal-content') {
    return buildSurface('support_report', '/support/report-illegal-content');
  }
  if (pathname === '/support/faq') {
    return buildSurface('support_faq', '/support/faq');
  }
  if (pathname === '/legal') {
    return buildSurface('legal', '/legal');
  }

  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 2 && segments[0] === 'games') {
    return buildSurface('game_details', '/games/:slug');
  }
  if (segments.length === 2 && segments[0] === 'news') {
    return buildSurface('news_article', '/news/:slug');
  }
  if (segments.length === 2 && segments[0] === 'legal') {
    return buildSurface('legal', '/legal/:slug');
  }
  if (
    segments.length === 3 &&
    segments[0] === 'support' &&
    segments[1] === 'faq'
  ) {
    return buildSurface('support_faq', '/support/faq/:segment');
  }
  if (
    segments.length === 4 &&
    segments[0] === 'support' &&
    segments[1] === 'faq'
  ) {
    return buildSurface('support_faq', '/support/faq/:category/:slug');
  }

  return null;
};

const isPlainObject = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
};

const isBoolean = (value) => typeof value === 'boolean';
const isAnalyticsEnabled = (value) => value === true;
const isSource = (value) => value === PUBLIC_TELEMETRY_SOURCE;
const isContractVersion = (value) => value === PUBLIC_TELEMETRY_CONTRACT_VERSION;
const isCookieTrigger = (value) => value === 'banner' || value === 'layout_menu';
const isNavigationType = (value) => (
  value === 'POP' || value === 'PUSH' || value === 'REPLACE'
);
const isActionId = (value) => (
  typeof value === 'string' &&
  PUBLIC_TELEMETRY_ACTION_CATALOG.some((action) => action.actionId === value)
);
const isSurfaceId = (value) => (
  typeof value === 'string' &&
  PUBLIC_TELEMETRY_SURFACE_CATALOG.some(
    (surface) => surface.collectionState === ACTIVE && surface.surfaceId === value,
  )
);
const isRouteTemplate = (value) => (
  typeof value === 'string' &&
  PUBLIC_TELEMETRY_SURFACE_CATALOG.some(
    (surface) => (
      surface.collectionState === ACTIVE && surface.routeTemplates.includes(value)
    ),
  )
);

const COMMON_PROPERTY_VALIDATORS = Object.freeze({
  contract_version: isContractVersion,
  source: isSource,
});

const EVENT_PROPERTY_VALIDATORS = Object.freeze({
  [PUBLIC_TELEMETRY_EVENTS.SESSION_STARTED]: Object.freeze({
    ...COMMON_PROPERTY_VALIDATORS,
    consent_marketing: isBoolean,
  }),
  [PUBLIC_TELEMETRY_EVENTS.CONSENT_GRANTED]: Object.freeze({
    ...COMMON_PROPERTY_VALIDATORS,
    consent_analytics: isAnalyticsEnabled,
    consent_marketing: isBoolean,
  }),
  [PUBLIC_TELEMETRY_EVENTS.COOKIE_PREFERENCES_OPENED]: Object.freeze({
    ...COMMON_PROPERTY_VALIDATORS,
    trigger: isCookieTrigger,
  }),
  [PUBLIC_TELEMETRY_EVENTS.COOKIE_PREFERENCES_CLOSED]: Object.freeze({
    ...COMMON_PROPERTY_VALIDATORS,
    has_existing_consent: isBoolean,
  }),
  [PUBLIC_TELEMETRY_EVENTS.SURFACE_VIEW]: Object.freeze({
    ...COMMON_PROPERTY_VALIDATORS,
    surface_id: isSurfaceId,
    route_template: isRouteTemplate,
    navigation_type: isNavigationType,
  }),
  [PUBLIC_TELEMETRY_EVENTS.ACTION_SELECTED]: Object.freeze({
    ...COMMON_PROPERTY_VALIDATORS,
    surface_id: isSurfaceId,
    action_id: isActionId,
  }),
});

export const PUBLIC_TELEMETRY_EVENT_CATALOG = Object.freeze(
  Object.fromEntries(
    Object.entries(EVENT_PROPERTY_VALIDATORS).map(([eventName, validators]) => [
      eventName,
      Object.freeze({
        contractVersion: PUBLIC_TELEMETRY_CONTRACT_VERSION,
        allowedProperties: Object.freeze(Object.keys(validators)),
        serverProperties: PUBLIC_TELEMETRY_SERVER_PROPERTIES,
      }),
    ]),
  ),
);

export const validatePublicTelemetryEvent = (input) => {
  if (!isPlainObject(input)) {
    return { ok: false, reason: 'event_payload_invalid' };
  }

  const validators = EVENT_PROPERTY_VALIDATORS[input.event];
  if (!validators) {
    return { ok: false, reason: 'event_not_allowed' };
  }

  if (!isPlainObject(input.properties)) {
    return { ok: false, reason: 'properties_invalid' };
  }

  const allowedProperties = Object.keys(validators);
  const receivedProperties = Object.keys(input.properties);
  if (receivedProperties.some((property) => !allowedProperties.includes(property))) {
    return { ok: false, reason: 'property_not_allowed' };
  }

  for (const property of allowedProperties) {
    if (!Object.prototype.hasOwnProperty.call(input.properties, property)) {
      return { ok: false, reason: 'property_missing' };
    }
    if (!validators[property](input.properties[property])) {
      return { ok: false, reason: 'property_invalid' };
    }
  }

  if (input.event === PUBLIC_TELEMETRY_EVENTS.SURFACE_VIEW) {
    const pair = `${input.properties.surface_id}\u0000${input.properties.route_template}`;
    if (!ACTIVE_SURFACE_ROUTE_PAIRS.has(pair)) {
      return { ok: false, reason: 'surface_route_mismatch' };
    }
  }

  if (input.event === PUBLIC_TELEMETRY_EVENTS.ACTION_SELECTED) {
    const pair = `${input.properties.action_id}\u0000${input.properties.surface_id}`;
    if (!ACTIVE_ACTION_SURFACE_PAIRS.has(pair)) {
      return { ok: false, reason: 'action_surface_mismatch' };
    }
  }

  return {
    ok: true,
    event: input.event,
    properties: { ...input.properties },
  };
};

export const createPublicTelemetryProperties = (event, properties = {}) => {
  const candidate = {
    ...(isPlainObject(properties) ? properties : {}),
    contract_version: PUBLIC_TELEMETRY_CONTRACT_VERSION,
    source: PUBLIC_TELEMETRY_SOURCE,
  };
  const validation = validatePublicTelemetryEvent({ event, properties: candidate });
  return validation.ok ? validation.properties : null;
};
